/**
 * @file ModalConverterTenant.jsx
 * @description Modal acionado pelo botão "Converter em Tenant" no LeadDrawer
 * (SystemHubModalVendas). Preenchido pelo vendedor durante a negociação;
 * ao enviar, o lead é movido para 'fechado_ganho' e o contrato é despachado
 * para assinatura via ClickSign.
 *
 * Consome: POST /api/vendas/lead/:id/converter/enviar
 *
 * v2 (Warm Linen — largura ampliada):
 *   - Layout em 2 colunas → cabe sem barra de rolagem em telas normais.
 *   - Canais de envio em MULTI-SELEÇÃO: E-mail e/ou WhatsApp.
 *   - Campo de mensagem do WhatsApp com sugestão automática (editável).
 *   - Estilo self-contained via <style> escopado (.mct-). Lógica de submit
 *     preservada; payload agora inclui `channels` e `whatsapp_message`
 *     (mantém `auth_method` derivado para compatibilidade com o backend atual).
 */

import React, { useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import {
  FaTimes, FaFileSignature, FaEnvelope, FaWhatsapp,
  FaBuilding, FaUserTie, FaLayerGroup, FaInfoCircle, FaCheck, FaMagic,
} from 'react-icons/fa';

const PLAN_OPTIONS = [
  { id: 'starter',  nome: 'Starter'  },
  { id: 'premium',  nome: 'Premium'  },
  { id: 'advanced', nome: 'Advanced' },
];

const formatBRL = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

// Sugestão de mensagem para o WhatsApp — calorosa, profissional e objetiva.
const buildWppMessage = (empresa, nome) => {
  const primeiro = (nome || '').trim().split(/\s+/)[0] || '';
  const ola = primeiro ? `Oi, ${primeiro}!` : 'Oi!';
  const emp = empresa?.trim() || 'sua empresa';
  return `${ola} 👋 Aqui é da BLB System.\n\n`
    + `O contrato da ${emp} já está pronto para assinatura — é rápido, 100% digital `
    + `e você assina direto pelo link, do celular mesmo. 📄✍️\n\n`
    + `Qualquer dúvida, estou por aqui. Bora deixar tudo pronto? 🚀`;
};

export default function ModalConverterTenant({ lead, onClose, onSuccess }) {
  const [form, setForm] = useState(() => ({
    nome_empresa: lead?.nome_empresa || '',
    documento_empresa: lead?.documento || '',
    plano_id: 'starter',
    valor_mensal: lead?.valor_potencial || '',
    max_licencas: 5,
    condicoes_comerciais: '',
    responsavel_nome: lead?.nome_contato || '',
    responsavel_email: lead?.email || '',
    responsavel_telefone: lead?.telefone || '',
    responsavel_documento: '',
    channels: ['email'],
    whatsapp_message: buildWppMessage(lead?.nome_empresa, lead?.nome_contato),
  }));
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const toggleChannel = (ch) => setForm((p) => {
    const has = p.channels.includes(ch);
    if (has && p.channels.length === 1) return p; // nunca fica sem nenhum canal
    const channels = has ? p.channels.filter((c) => c !== ch) : [...p.channels, ch];
    return { ...p, channels };
  });

  const useWppSuggestion = () =>
    set('whatsapp_message', buildWppMessage(form.nome_empresa, form.responsavel_nome));

  const wppOn = form.channels.includes('whatsapp');
  const emailOn = form.channels.includes('email');
  const nomeOk = form.responsavel_nome.trim().split(/\s+/).filter(Boolean).length >= 2;
  const valorFmt = useMemo(() => formatBRL(form.valor_mensal), [form.valor_mensal]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.nome_empresa.trim()) return toast.warn('Informe o nome da empresa.');
    if (!form.responsavel_nome.trim() || form.responsavel_nome.trim().split(' ').length < 2) {
      return toast.warn('Informe nome e sobrenome do responsável (exigido para a assinatura).');
    }
    if (!form.channels.length) return toast.warn('Selecione ao menos um canal de envio.');
    if (emailOn && !form.responsavel_email) {
      return toast.warn('Informe o e-mail do responsável para envio por e-mail.');
    }
    if (wppOn && !form.responsavel_telefone) {
      return toast.warn('Informe o telefone do responsável para envio por WhatsApp.');
    }

    // auth_method derivado mantém compat com o backend atual (espera 'email'|'whatsapp').
    const auth_method = emailOn ? 'email' : 'whatsapp';
    const payload = {
      ...form,
      auth_method,
      whatsapp_message: wppOn ? form.whatsapp_message : null,
    };

    setSaving(true);
    try {
      const { data } = await api.post(`/api/vendas/lead/${lead.id}/converter/enviar`, payload);
      toast.success('Contrato enviado para assinatura! Lead movido para Fechado.');
      onSuccess?.(data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Não foi possível enviar o contrato.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mct-overlay" onClick={onClose}>
      <style>{MCT_CSS}</style>

      <div className="mct-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        {/* Cabecalho */}
        <header className="mct-head">
          <div className="mct-head__icon"><FaFileSignature /></div>
          <div className="mct-head__txt">
            <h3>Converter em Tenant</h3>
            <span>Envie o contrato para assinatura e abra o ticket de provisionamento</span>
          </div>
          <button type="button" className="mct-close" onClick={onClose} aria-label="Fechar"><FaTimes /></button>
        </header>

        {/* Corpo */}
        <div className="mct-body">
          <div className="mct-callout">
            <FaInfoCircle className="mct-callout__icon" />
            <p>
              Ao enviar, este lead vai para <strong>Fechado (ganho)</strong> e o contrato é despachado
              via <strong>ClickSign</strong>. Assim que assinado, abrimos automaticamente um ticket
              para o CS criar o tenant.
            </p>
          </div>

          <form className="mct-cols" onSubmit={submit}>
            {/* Coluna esquerda */}
            <div className="mct-col">
              <div className="mct-section">
                <div className="mct-section__label"><FaBuilding /> Dados da empresa</div>
                <label className="mct-field">
                  <span className="mct-lbl">Nome da empresa <b className="mct-req">*</b></span>
                  <input className="mct-input" value={form.nome_empresa}
                    onChange={(e) => set('nome_empresa', e.target.value)} placeholder="Ex: Guincho Oliveira" />
                </label>
                <label className="mct-field">
                  <span className="mct-lbl">CNPJ / CPF</span>
                  <input className="mct-input" value={form.documento_empresa}
                    onChange={(e) => set('documento_empresa', e.target.value)} placeholder="Apenas números" />
                </label>
              </div>

              <div className="mct-section">
                <div className="mct-section__label"><FaLayerGroup /> Plano &amp; valores</div>
                <div className="mct-field">
                  <span className="mct-lbl">Plano</span>
                  <div className="mct-segment">
                    {PLAN_OPTIONS.map((p) => (
                      <button key={p.id} type="button"
                        className={`mct-seg-opt ${form.plano_id === p.id ? 'is-active' : ''}`}
                        onClick={() => set('plano_id', p.id)}>
                        {form.plano_id === p.id && <FaCheck size={11} />} {p.nome}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mct-grid mct-grid--2">
                  <label className="mct-field">
                    <span className="mct-lbl">Valor mensal</span>
                    <div className="mct-adorn">
                      <span className="mct-adorn__prefix">R$</span>
                      <input className="mct-input mct-input--bare" type="number" min="0" step="0.01"
                        value={form.valor_mensal} onChange={(e) => set('valor_mensal', e.target.value)} placeholder="0,00" />
                    </div>
                    {valorFmt && <span className="mct-help">{valorFmt} por mês</span>}
                  </label>
                  <label className="mct-field">
                    <span className="mct-lbl">Nº de licenças</span>
                    <input className="mct-input" type="number" min="1"
                      value={form.max_licencas} onChange={(e) => set('max_licencas', parseInt(e.target.value, 10) || 1)} />
                  </label>
                </div>
                <label className="mct-field">
                  <span className="mct-lbl">Condições comerciais <em>(opcional)</em></span>
                  <textarea className="mct-input mct-textarea" value={form.condicoes_comerciais}
                    onChange={(e) => set('condicoes_comerciais', e.target.value)}
                    placeholder="Descontos, prazo de fidelidade, observações do fechamento…" />
                </label>
              </div>
            </div>

            {/* Coluna direita */}
            <div className="mct-col">
              <div className="mct-section">
                <div className="mct-section__label"><FaUserTie /> Responsável pela assinatura</div>
                <label className="mct-field">
                  <span className="mct-lbl">Nome completo (contratante) <b className="mct-req">*</b></span>
                  <input className={`mct-input ${form.responsavel_nome && !nomeOk ? 'is-warn' : ''}`}
                    value={form.responsavel_nome} onChange={(e) => set('responsavel_nome', e.target.value)} placeholder="Nome e sobrenome" />
                  {form.responsavel_nome && !nomeOk && (
                    <span className="mct-help mct-help--warn">A ClickSign exige nome e sobrenome.</span>
                  )}
                </label>
                <label className="mct-field">
                  <span className="mct-lbl">E-mail {emailOn && <b className="mct-req">*</b>}</span>
                  <input className="mct-input" type="email" value={form.responsavel_email}
                    onChange={(e) => set('responsavel_email', e.target.value)} placeholder="responsavel@empresa.com" />
                </label>
                <label className="mct-field">
                  <span className="mct-lbl">Telefone (WhatsApp) {wppOn && <b className="mct-req">*</b>}</span>
                  <input className="mct-input" value={form.responsavel_telefone}
                    onChange={(e) => set('responsavel_telefone', e.target.value)} placeholder="DDD + número" />
                </label>
              </div>

              <div className="mct-section">
                <div className="mct-section__label"><FaFileSignature /> Como enviar o contrato</div>
                <div className="mct-field">
                  <span className="mct-lbl">Canais de envio <em>(pode marcar os dois)</em></span>
                  <div className="mct-segment mct-segment--multi">
                    <button type="button"
                      className={`mct-seg-opt ${emailOn ? 'is-active' : ''}`}
                      onClick={() => toggleChannel('email')} aria-pressed={emailOn}>
                      {emailOn && <FaCheck size={10} />} <FaEnvelope /> E-mail
                    </button>
                    <button type="button"
                      className={`mct-seg-opt mct-seg-opt--wpp ${wppOn ? 'is-active' : ''}`}
                      onClick={() => toggleChannel('whatsapp')} aria-pressed={wppOn}>
                      {wppOn && <FaCheck size={10} />} <FaWhatsapp /> WhatsApp
                    </button>
                  </div>
                  <span className="mct-help">
                    {emailOn && wppOn
                      ? 'A solicitação será enviada por e-mail e por WhatsApp.'
                      : emailOn ? 'A solicitação será enviada por e-mail.'
                        : 'A solicitação será enviada pelo WhatsApp.'}
                  </span>
                </div>

                {wppOn && (
                  <div className="mct-field mct-wpp">
                    <div className="mct-wpp__head">
                      <span className="mct-lbl"><FaWhatsapp style={{ color: '#25D366' }} /> Mensagem do WhatsApp</span>
                      <button type="button" className="mct-wpp__suggest" onClick={useWppSuggestion}>
                        <FaMagic size={10} /> Sugestão
                      </button>
                    </div>
                    <textarea className="mct-input mct-textarea mct-wpp__area" value={form.whatsapp_message}
                      onChange={(e) => set('whatsapp_message', e.target.value)} maxLength={900}
                      placeholder="Escreva a mensagem que o cliente vai receber no WhatsApp…" />
                    <span className="mct-help">{form.whatsapp_message.length}/900 · emojis são bem-vindos 😉</span>
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Rodape fixo */}
        <footer className="mct-foot">
          <button type="button" className="mct-btn mct-btn--ghost" onClick={onClose} disabled={saving}>Cancelar</button>
          <button type="button" className="mct-btn mct-btn--primary" onClick={submit} disabled={saving}>
            {saving
              ? (<><span className="mct-spinner" /> Enviando…</>)
              : (<><FaFileSignature /> Enviar para Assinatura</>)}
          </button>
        </footer>
      </div>
    </div>
  );
}

const MCT_CSS = `
.mct-overlay{
  position:fixed; inset:0; z-index:1000;
  display:flex; align-items:center; justify-content:center; padding:20px;
  background:rgba(16,28,93,.42); backdrop-filter:blur(3px);
  animation:mct-fade .18s ease;
  font-family:'DM Sans',system-ui,-apple-system,Segoe UI,Roboto,sans-serif;
}
@keyframes mct-fade{from{opacity:0}to{opacity:1}}
@keyframes mct-pop{from{opacity:0;transform:translateY(8px) scale(.985)}to{opacity:1;transform:none}}
@keyframes mct-spin{to{transform:rotate(360deg)}}

.mct-modal{
  --teal:#0d7377; --teal-d:#0a5a5d; --ink:#1a2130; --muted:#6b7280;
  --line:#e6e8ee; --bg-soft:#f6f8f9;
  width:100%; max-width:880px; max-height:92vh;
  display:flex; flex-direction:column;
  background:#fff; border-radius:20px; overflow:hidden;
  box-shadow:0 24px 60px -12px rgba(16,28,93,.32), 0 0 0 1px rgba(16,28,93,.04);
  animation:mct-pop .22s cubic-bezier(.2,.8,.25,1);
}

.mct-head{
  display:flex; align-items:center; gap:14px; padding:20px 24px;
  border-bottom:1px solid var(--line); background:linear-gradient(180deg,#f3faf9,#fff);
}
.mct-head__icon{
  flex:0 0 auto; width:44px; height:44px; border-radius:12px; display:grid; place-items:center;
  font-size:1.05rem; color:#fff; background:linear-gradient(135deg,var(--teal),var(--teal-d));
  box-shadow:0 4px 12px -2px rgba(13,115,119,.5);
}
.mct-head__txt{flex:1 1 auto; min-width:0}
.mct-head__txt h3{margin:0; font-size:1.12rem; font-weight:700; color:var(--ink); letter-spacing:-.01em}
.mct-head__txt span{display:block; margin-top:2px; font-size:.82rem; color:var(--muted)}
.mct-close{
  flex:0 0 auto; width:34px; height:34px; border-radius:9px; cursor:pointer;
  border:1px solid var(--line); background:#fff; color:var(--muted); display:grid; place-items:center; transition:.15s;
}
.mct-close:hover{background:#fdecef; color:#b91c1c; border-color:#f6d0d6}

.mct-body{overflow-y:auto; padding:20px 24px}
.mct-callout{
  display:flex; gap:10px; align-items:flex-start;
  background:#eaf5f5; border:1px solid #cfe8e8; border-radius:12px; padding:12px 14px; margin-bottom:20px;
}
.mct-callout__icon{flex:0 0 auto; margin-top:2px; color:var(--teal); font-size:.95rem}
.mct-callout p{margin:0; font-size:.84rem; line-height:1.5; color:#2c4a4b}
.mct-callout strong{color:var(--teal-d); font-weight:700}

.mct-cols{display:grid; grid-template-columns:1fr 1fr; gap:26px}
@media(max-width:720px){.mct-cols{grid-template-columns:1fr; gap:4px}}
.mct-col{min-width:0}

.mct-section{margin-bottom:20px}
.mct-col:last-child .mct-section:last-child{margin-bottom:0}
.mct-section__label{
  display:flex; align-items:center; gap:7px; margin-bottom:12px;
  font-size:.72rem; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:var(--teal-d);
}
.mct-section__label svg{font-size:.8rem; opacity:.85}

.mct-grid{display:grid; gap:14px}
.mct-grid--2{grid-template-columns:1fr 1fr}
@media(max-width:520px){.mct-grid--2{grid-template-columns:1fr}}

.mct-field{display:flex; flex-direction:column; gap:6px; margin-bottom:14px}
.mct-section .mct-field:last-child{margin-bottom:0}
.mct-grid .mct-field{margin-bottom:0}
.mct-lbl{display:flex; align-items:center; gap:6px; font-size:.8rem; font-weight:600; color:var(--ink)}
.mct-lbl em{font-weight:400; color:var(--muted); font-style:normal}
.mct-req{color:#e0574f}

.mct-input{
  width:100%; box-sizing:border-box; padding:10px 13px; font-size:.9rem; color:var(--ink);
  background:#fff; border:1px solid var(--line); border-radius:10px;
  transition:border-color .15s, box-shadow .15s; font-family:inherit;
}
.mct-input::placeholder{color:#aab0bb}
.mct-input:focus{outline:none; border-color:var(--teal); box-shadow:0 0 0 3px rgba(13,115,119,.14)}
.mct-input.is-warn{border-color:#e6a23c; box-shadow:0 0 0 3px rgba(230,162,60,.14)}
.mct-textarea{min-height:72px; resize:vertical; line-height:1.45}

.mct-adorn{display:flex; align-items:center; border:1px solid var(--line); border-radius:10px; background:#fff; overflow:hidden; transition:border-color .15s, box-shadow .15s}
.mct-adorn:focus-within{border-color:var(--teal); box-shadow:0 0 0 3px rgba(13,115,119,.14)}
.mct-adorn__prefix{padding:10px 12px; font-size:.85rem; font-weight:700; color:var(--muted); background:var(--bg-soft); border-right:1px solid var(--line)}
.mct-input--bare{border:none; border-radius:0}
.mct-input--bare:focus{box-shadow:none}

.mct-help{font-size:.74rem; color:var(--muted)}
.mct-help--warn{color:#b7791f; font-weight:600}

.mct-segment{display:flex; gap:8px; flex-wrap:wrap}
.mct-segment--multi .mct-seg-opt{flex:1 1 0}
.mct-seg-opt{
  display:inline-flex; align-items:center; justify-content:center; gap:7px;
  padding:10px 16px; cursor:pointer; font-size:.85rem; font-weight:600;
  color:#4b5563; background:#fff; border:1px solid var(--line); border-radius:10px; transition:.15s; font-family:inherit;
}
.mct-seg-opt:hover{border-color:#c9ced8; background:var(--bg-soft)}
.mct-seg-opt.is-active{color:#fff; border-color:var(--teal); background:linear-gradient(135deg,var(--teal),var(--teal-d)); box-shadow:0 3px 10px -3px rgba(13,115,119,.5)}
.mct-seg-opt--wpp.is-active{border-color:#1ea952; background:linear-gradient(135deg,#25D366,#128C4A); box-shadow:0 3px 10px -3px rgba(18,140,74,.5)}

.mct-wpp__head{display:flex; align-items:center; justify-content:space-between; gap:8px}
.mct-wpp__suggest{
  display:inline-flex; align-items:center; gap:5px; cursor:pointer;
  font-size:.72rem; font-weight:700; color:var(--teal-d);
  background:#eaf5f5; border:1px solid #cfe8e8; border-radius:8px; padding:4px 9px; transition:.15s; font-family:inherit;
}
.mct-wpp__suggest:hover{background:#d9eeed}
.mct-wpp__area{min-height:118px; background:#f7fbf8; border-color:#d7e9dd; white-space:pre-wrap}
.mct-wpp__area:focus{border-color:#1ea952; box-shadow:0 0 0 3px rgba(37,211,102,.16)}

.mct-foot{display:flex; justify-content:flex-end; gap:10px; padding:16px 24px; border-top:1px solid var(--line); background:var(--bg-soft)}
.mct-btn{display:inline-flex; align-items:center; gap:8px; padding:11px 20px; font-size:.9rem; font-weight:700; cursor:pointer; border-radius:11px; border:1px solid transparent; transition:.15s; font-family:inherit}
.mct-btn:disabled{opacity:.6; cursor:not-allowed}
.mct-btn--ghost{background:#fff; border-color:var(--line); color:#4b5563}
.mct-btn--ghost:hover:not(:disabled){background:#fff; border-color:#c9ced8}
.mct-btn--primary{color:#fff; background:linear-gradient(135deg,var(--teal),var(--teal-d)); box-shadow:0 4px 14px -3px rgba(13,115,119,.55)}
.mct-btn--primary:hover:not(:disabled){filter:brightness(1.05)}
.mct-spinner{width:14px; height:14px; border-radius:50%; border:2px solid rgba(255,255,255,.45); border-top-color:#fff; animation:mct-spin .7s linear infinite}
`;